import { useState, useEffect, useCallback, useRef } from 'react';

export const usePersistedForm = <T extends object>(key: string, initialState: T) => {
  const [state, setState] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all required fields from initialState exist in the saved state
        return { ...initialState, ...parsed };
      }
    } catch (error) {
    }
    return initialState;
  });

  // Keep track of the last saved state to prevent unnecessary updates
  const lastSavedState = useRef<T>(state);
  // Track if we're in a sleep/wake cycle
  const isSleeping = useRef(false);

  const updateState = useCallback((newState: T | ((prevState: T) => T)) => {
    setState(prevState => {
      const updatedState = typeof newState === 'function' ? newState(prevState) : newState;
      
      // Only save to localStorage if the state has actually changed
      if (JSON.stringify(updatedState) !== JSON.stringify(lastSavedState.current)) {
        try {
          localStorage.setItem(key, JSON.stringify(updatedState));
          lastSavedState.current = updatedState;
        } catch (error) {
        }
      }
      
      return updatedState;
    });
  }, [key]);

  // Add event listener for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          const newState = JSON.parse(e.newValue);
          // Only update if the new state is different from our current state
          if (JSON.stringify(newState) !== JSON.stringify(state)) {
            setState(prevState => ({ ...prevState, ...newState }));
            lastSavedState.current = newState;
          }
        } catch (error) {
        }
      }
    };

    // Handle visibility changes (sleep/wake cycles)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        isSleeping.current = true;
      } else if (document.visibilityState === 'visible' && isSleeping.current) {
        // Only restore state if we were sleeping
        try {
          const saved = localStorage.getItem(key);
          if (saved) {
            const parsed = JSON.parse(saved);
            // Only update if the saved state is different from our current state
            if (JSON.stringify(parsed) !== JSON.stringify(state)) {
              setState(prevState => ({ ...prevState, ...parsed }));
              lastSavedState.current = parsed;
            }
          }
        } catch (error) {
        }
        isSleeping.current = false;
      }
    };

    // Handle page unload/navigation
    const handleBeforeUnload = () => {
      // Clear the state from localStorage when leaving the page
      localStorage.removeItem(key);
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also clear state when component unmounts
      localStorage.removeItem(key);
    };
  }, [key, state]);

  return [state, updateState] as const;
}; 